<script lang="ts">
  import Input from "./Input.svelte";
  import { getUserAddressPredictions, isAddressStreetNumberRequired } from "../../lib/helpers/address";
  import PredictionItem from "./PredictionItem.svelte";
  import type { SearchAddressPrediction } from "../../lib/api/address";
  import { addressesStore } from "../../lib/stores/addresses";
  import Spinner from "../Spinner.svelte";
  import { clickOutside } from "../../lib/utils";
  import { settingsStore } from "../../lib/stores/settings";
  import { onMount } from "svelte";

  export let placeholder: string;
  export let onAddressSelected: (selectedAddress: SearchAddressPrediction | null) => void;
  export let invalidMessage: string | undefined = undefined;
  export let value = "";
  export let onEnterAddressManually: () => void;
  let addressDescription = "";

  let isLoading = false;
  let predictionResults: Array<SearchAddressPrediction> = [];

  onMount(() => {
    addressDescription = value;
  });

  const onAddressPredictionSelected = (selectedAddress: SearchAddressPrediction) => {
    onAddressSelected(selectedAddress);
    showPredictionResults = false;
    addressDescription = selectedAddress.formatted_address;
    predictionResults = [];
  };

  const handleAddressInput = async (inputValue: string) => {
    onAddressSelected(null);

    if (!inputValue) {
      predictionResults = [];
      return;
    }

    if (isAddressStreetNumberRequired(inputValue, countries)) {
      $addressesStore.addressInvalid = true;
      return;
    }

    isLoading = true;
    showPredictionResults = true;
    try {
      predictionResults = await getUserAddressPredictions(inputValue, countries);
      $addressesStore.noAddressesFound = predictionResults.length < 1;
    } catch (error) {
      predictionResults = [];
      $addressesStore.noAddressesFound = true;
    } finally {
      isLoading = false;
      if (predictionResults?.length === 0) {
        showPredictionResults = false;
      }
    }
  };

  const onChange = async (userInputAddress: string | undefined | boolean, event?: KeyboardEvent) => {
    if (!userInputAddress || isLoading) {
      return;
    }

    value = userInputAddress as string;
    predictionResults = [];
    onAddressSelected(null);
    $addressesStore.addressInvalid = false;
    $addressesStore.noAddressesFound = false;

    showPredictionResults = true;
    if (event?.key === "Enter" || event?.type === 'blur') {
      await handleAddressInput(value);
    }
  };

  const onEnterAddressManuallyClick = () => {
    onEnterAddressManually();
  };

  $: showPredictionResults = false;
  $: countries = $settingsStore.countries as string[];
</script>

<div class="relative">
  <Input
    isDisabled={isLoading}
    inputProps={{
      autocomplete: "street-address",
      id: "address"
    }}
    bind:value={addressDescription}
    {placeholder}
    {onChange}
    {invalidMessage}
    handleOnEnterKeyPress handleOnBlurEvent /> 
  {#if invalidMessage && $settingsStore?.isSocialGifting}
    <span class="text-xs mt-1 text-[var(--color-red-50)]">
      <button class="underline decoration-solid" on:click={onEnterAddressManuallyClick}>Enter address manually</button>
    </span>
  {/if}
  {#if showPredictionResults}
    <div
      aria-label="Address prediction"
      role="dialog"
      use:clickOutside
      on:outclick={() => (showPredictionResults = false)}
      class="dropdown absolute top-18 left-0 rounded-lg drop-shadow-md z-10 bg-white py-3 px-2 lg:min-w-96 w-full overflow-y-auto">
      {#if isLoading}
        <div class="flex items-center justify-center w-full min-h-12">
          <Spinner />
        </div>
      {:else if predictionResults.length === 0}
        <button class="py-3 px-4 flex pointer max-w-full" on:click={() => handleAddressInput(value)}>
          <span class="title font-semibold s-W8GvqgY4Fxo6 underline text-[var(--color-grayscale-90)]"
            >Find my address</span>
        </button>
      {:else}
        {#each predictionResults as prediction}
          <PredictionItem {prediction} predictionSelected={onAddressPredictionSelected} />
        {/each}
      {/if}
    </div>
  {/if}
</div>
